import { SITE_VOICE } from '../siteVoice';

/** State / mode explainer copy for terminal app demo. */

export type StateMgmtDemoMode = 'accept' | 'plan' | 'chat';

export interface StateMgmtDemoModeConfig {
  name: string;
  indicator: string;
  color: string;
}

export const STATE_MGMT_MODES: Record<
  StateMgmtDemoMode,
  StateMgmtDemoModeConfig
> = {
  accept: {
    name: 'accept edits on',
    indicator: '>>',
    color: 'terminal-magenta',
  },
  plan: {
    name: 'plan mode',
    indicator: '??',
    color: 'terminal-yellow',
  },
  chat: {
    name: 'chat mode',
    indicator: '~~',
    color: 'terminal-cyan',
  },
};

export const STATE_MGMT_MODE_ORDER: StateMgmtDemoMode[] = [
  'accept',
  'plan',
  'chat',
];

export type StateMgmtExplainerStep =
  | 'overview'
  | 'memory'
  | 'rendering'
  | 'input-handling'
  | 'persistence';

export interface StateMgmtExplainerSlide {
  title: string;
  description: string;
}

export const STATE_MGMT_EXPLAINER_STEPS: Record<
  StateMgmtExplainerStep,
  StateMgmtExplainerSlide
> = {
  overview: {
    title: 'State in Terminal Apps',
    description:
      'Terminal apps maintain state in memory just like GUI apps. The difference is how they display it: by printing characters to specific positions. When state changes, they redraw the relevant parts of the screen.',
  },
  memory: {
    title: 'Where State Lives',
    description:
      "The app keeps variables in memory: currentMode, inputBuffer, history, etc. These are just regular program variables. The terminal itself doesn't store your app's state—it only displays whatever characters you send it.",
  },
  rendering: {
    title: 'Rendering State Changes',
    description:
      'When the mode changes, the app: 1) Updates the variable in memory, 2) Moves the cursor to where the indicator is displayed, 3) Clears that region, 4) Prints the new indicator with appropriate colors. The user sees a seamless transition.',
  },
  'input-handling': {
    title: 'Input Triggers State Changes',
    description:
      "Key combinations like Shift+Tab are just byte sequences. The app receives these bytes, recognizes the pattern, updates internal state, and re-renders. The terminal doesn't know anything about 'modes'—it just passes bytes through.",
  },
  persistence: {
    title: 'Persistence & Sessions',
    description:
      "Terminal apps can save state to files (config, history) but lose in-memory state when they exit. Some apps use the terminal's alternate screen buffer—when they exit, the original screen content is restored, as if the app was never there.",
  },
};

export const STATE_MGMT_CYCLE = {
  sectionTitle: 'The State Update Cycle',
  footer:
    'The terminal never "knows" about modes. It just displays whatever characters the app sends. All the intelligence—tracking state, responding to input, deciding what to draw—lives in the app.',
  input: {
    title: 'Input',
    subtitle: 'User presses Shift+Tab',
    snippet: 'ESC [ Z',
  },
  process: {
    title: 'Process',
    subtitle: 'App recognizes sequence',
    snippet: 'mode = nextMode()',
  },
  render: {
    title: 'Render',
    subtitle: 'Redraw mode indicator',
    snippet: 'print(indicator)',
  },
} as const;

export const STATE_MGMT_INTERACTIVE_UI = {
  placeholder: SITE_VOICE.stateMgmtInteractivePlaceholder,
  shiftTabHint: '(shift+tab to cycle)',
  cycleButton: 'Cycle Mode (Shift+Tab)',
  showInspector: 'Show State Inspector',
  hideInspector: 'Hide State Inspector',
  inspectorTitle: 'State Inspector (What the App Remembers)',
  keyShiftTabFeedback: 'Shift+Tab → cycle mode',
  keyEnterFeedback: 'Enter → submit',
} as const;
